// @givmo/app-charity — the console's half of Board's director pages (CBG-SEC2 SEC2-WEB; contract §2.1,
// §2.2).
//
// Rendered around the whole director family. Two hosts, one for each seam the pages lean on:
//
//   * `@givmo/step-up`'s, with the console's own adapter (`useCharityStepUpHost`, Compliance's): the
//     ONE "Confirm it's you" sheet every signing act and gated read opens. Board draws no sheet of
//     its own (Account Security, D-AS-17).
//   * Board's director host: the signed-in account, the ways in (`director-ways-in.tsx`), signing
//     out, Account Security's setup offer (A1, `@givmo/passkeys`' `PasskeySetupOffer`: its heading
//     and line Board places, its card beneath) and the device's own word for a passkey ("Touch ID"
//     on a Mac, "Face ID" on an iPhone; A1's rule, `passkeyMethodWord`), and the person's own
//     "Sign-in & security" card (A4, `SignInSecurityCard`). A director who isn't one of the
//     organization's officers has no Settings to reach that card through, so Board's account menu
//     leads to a director page that places it, in the employer portal's words for the same page.
//     The menu's row shows only while the card's read is served (its dark gate), as on the console's
//     own menu.
//
// The offer and the card add a passkey through the sheet, so both render inside the step-up host
// below. Both also take the console's authenticator-app controls (AS-WEB14), so a director can set up
// an authenticator from the offer, where the web offers one to the account, and from the card.
//
// Under a sign-in lock (AS-WEB23, frame E3) the offer's heading is the lock's words
// (`signInLockOfferWords`), and the offer beneath them sets nothing up. The words follow the account's
// security read as the cache holds it: this host makes no read of its own (AS-WEB15), and Board asks
// the step-up host's `prepare`, which reads it, before it ever opens the offer.

import { useMemo, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { StepUpHostProvider } from '@givmo/step-up';
import {
  BoardAskSlotsProvider,
  BoardDirectorHostProvider,
  type AskSlots,
  type BoardDirectorHost,
} from '@givmo/board';
import { sdk } from '@givmo/api-client';
import { FundraiserPageWizard } from '@givmo/fundraisers-module';
import { ShareKit } from '@givmo/org-console';
import { getDonateWebBaseUrl } from '../../lib/env';
import { toast } from '@givmo/design-system';
import {
  PasskeySetupOffer,
  SignInSecurityCard,
  detectPasskeyDevice,
  passkeyMethodWord,
  passkeySetupOfferWords,
  signInLockOfferWords,
  useMyPasskeysOnDemand,
  useSignInLock,
} from '@givmo/passkeys';
import { useAuth } from '../../auth/auth-context';
import { sanitizeRedirectTarget } from '../../router/redirect-target';
import { directorEmailSearch } from '../auth/director-arrival';
import { useCharityStepUpHost } from '../step-up/charity-step-up-host';
import { stashDirectorInvitation } from './director-invitation-handoff';
import { carryDirectorLockNoticeLine } from './director-lock-notice-line';
import { CharityDirectorWaysIn } from './director-ways-in';
import { DIRECTOR_ROUTES } from './director-routes';

/** A fundraiser's public page on the donate app. */
function donationUrl(fundraiserId: string): string {
  return `${getDonateWebBaseUrl().replace(/\/$/, '')}/donation?fundraiserId=${encodeURIComponent(fundraiserId)}`;
}

/**
 * EMC-WEB4: what the Board Ask's director page rents. The share kit is `@givmo/org-console`'s, the
 * one built for every sharing surface; every kit act records the seat's "shared". The fundraiser
 * page wizard is `@givmo/fundraisers-module`'s.
 */
const BOARD_ASK_SLOTS: AskSlots = {
  renderShareKit: ({ campaignId, collateralItemIds, bare, onShared }) => (
    <ShareKit
      entityType="fundraiser"
      entityId={campaignId}
      collateralItemIds={collateralItemIds}
      {...(bare === true ? { bare: true } : {})}
      onShared={onShared}
    />
  ),
  renderPageWizard: ({ campaignId, existing, onBack, onExit, onPageChanged }) => (
    <FundraiserPageWizard
      campaignId={campaignId}
      {...(existing === undefined ? {} : { existing })}
      shareLink={async () => {
        const { data } = await sdk.shareKitGetMyShareKit({
          query: { entity_type: 'fundraiser', entity_id: campaignId },
          throwOnError: true,
        });
        return data.share_url;
      }}
      pageUrl={(page) => donationUrl(page.fundraiserId)}
      back={{ label: 'Back to the ask', onBack }}
      onExit={onExit}
      onPageChanged={onPageChanged}
    />
  ),
  pageUrl: donationUrl,
};

export function CharityDirectorHost({ children }: { children: ReactNode }) {
  const { user, signOut, authenticator } = useAuth();
  const stepUp = useCharityStepUpHost();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // This browser's platform decides the word ("Touch ID", "Face ID", "Windows Hello", …): read once.
  const device = useMemo(() => detectPasskeyDevice(), []);
  const uid = user?.uid ?? null;
  // The card's own dark gate, one shared read: the menu's row shows only where the card would — kept
  // through a failed re-read and withdrawn only by the dark 404, as the console's own row is — and,
  // as there (AS-WEB16), read only as the director reaches for the menu, never per page or focus.
  const { passkeys: menuPasskeys, read: readMenuPasskeys } = useMyPasskeysOnDemand(uid);
  const passkeysServed = menuPasskeys !== undefined;
  // The offer's words under a sign-in lock (AS-WEB23), from the cache only, never a read per page.
  const lock = useSignInLock(uid, { read: false });

  const host = useMemo<BoardDirectorHost>(
    () => ({
      account:
        user === null
          ? null
          : { displayName: user.displayName ?? null, email: user.email ?? null, uid: user.uid },
      renderWaysIn: (props) => <CharityDirectorWaysIn {...props} />,
      // The console's sign-out (`auth/use-sign-out.ts`), landing where the director was asked to go
      // — the invitation for "Use another account", else the door — rather than the console's
      // landing. Signing out ends the signing window with the session.
      signOut: async (returnTo) => {
        try {
          await signOut();
          queryClient.clear();
          await navigate({
            to: sanitizeRedirectTarget(returnTo) ?? DIRECTOR_ROUTES.signIn,
            replace: true,
          });
          // Twice, as the console's sign-out does: the first drops the previous account's data at
          // once, the second runs with the signed-in tree gone.
          queryClient.clear();
        } catch {
          toast.error('We couldn’t sign you out. Please try again.');
        }
      },
      // CBG-SEC2-L4 (D-CBG-23; frames a1, a2, a2c): "Create new account" for a director whose
      // account another organization's sign-in lock binds. Board asks for it signed out (it signs a
      // signed-in account out first, back to the invitation, so the form's own guard never meets a
      // session the console hasn't seen end). The console's own create form opens in its director
      // arrival, with the invitation's two lines and Board's "use another Givmo account" arrival,
      // which `directorEmailSearch` carries through both email forms, so a lock refusal there offers
      // no organization door back to the account just left. No lock line rides along: this way is
      // already the other account's.
      // CBG-SEC2-L4-MOVE (frame b3c): the same form for a director moving their seat, with the
      // move's two lines instead, back to the move once the new account is confirmed.
      createAnotherAccount: async ({ returnTo, invitation, move }) => {
        if (move !== undefined) stashDirectorInvitation({ ...move, purpose: 'seat_move' });
        else if (invitation !== undefined) stashDirectorInvitation(invitation);
        carryDirectorLockNoticeLine(undefined);
        await navigate({
          to: DIRECTOR_ROUTES.signUpEmail,
          search: directorEmailSearch(
            sanitizeRedirectTarget(returnTo) ?? DIRECTOR_ROUTES.home,
            'another_account',
          ),
        });
      },
      // CBG-SEC2-L4: who locks the account, for Board's pointer (frame a1), from the cache only:
      // Account Security's rule names the employer only where its own block says its lock binds.
      lockingOrganizationName: lock.organizationName,
      // CBG-SEC2-L4 (frames a0, a2): today only an employer's sign-in lock binds an account (AS-BE81).
      lockingOrganizationNoun: 'employer',
      setupOffer: {
        ...(lock.active ? signInLockOfferWords(lock) : passkeySetupOfferWords(device)),
        render: ({ variant, onSettled }) => (
          <PasskeySetupOffer
            variant={variant}
            onSettled={onSettled}
            device={device}
            // Account Security's authenticator setup (AS-WEB14): for an account the web offers one
            // to, the offer draws "Use an authenticator app instead" and runs the setup itself.
            authenticator={authenticator}
            uid={uid}
            // Board's shell places the words above the offer, the lock's included.
            wordsPlaced
          />
        ),
      },
      signInSecurity: {
        title: 'Sign-in & security',
        description: 'For your Givmo account, everywhere you sign in.',
        available: passkeysServed,
        read: () => void readMenuPasskeys(),
        render: () => <SignInSecurityCard uid={uid} headless authenticator={authenticator} />,
      },
      methodWord: passkeyMethodWord(device),
    }),
    [
      user,
      signOut,
      authenticator,
      queryClient,
      navigate,
      device,
      uid,
      passkeysServed,
      readMenuPasskeys,
      lock,
    ],
  );

  return (
    <StepUpHostProvider adapter={stepUp}>
      <BoardDirectorHostProvider value={host}>
        <BoardAskSlotsProvider slots={BOARD_ASK_SLOTS}>{children}</BoardAskSlotsProvider>
      </BoardDirectorHostProvider>
    </StepUpHostProvider>
  );
}
